import { clsx } from 'clsx'
import {
  Activity,
  AlertTriangle,
  Bell,
  Bug,
  ClipboardList,
  CreditCard,
  FileText,
  Globe,
  HardDrive,
  LayoutDashboard,
  Mail,
  Monitor,
  MonitorCheck,
  Network,
  Server,
  Settings,
  Shield,
  ShieldAlert,
  Users,
  X,
} from 'lucide-react'
import { NavLink } from 'react-router'
import { useAuth } from '@/lib/auth-context'

const ICON_MAP = {
  LayoutDashboard,
  Users,
  Mail,
  Server,
  HardDrive,
  Globe,
  Activity,
  AlertTriangle,
  ShieldAlert,
  ClipboardList,
  Monitor,
  MonitorCheck,
  Network,
  CreditCard,
  Bell,
  FileText,
  Shield,
  Bug,
  Settings,
} as const

interface NavItem {
  path: string
  label: string
  icon: keyof typeof ICON_MAP
  superAdminOnly?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { path: '/', label: 'Overview', icon: 'LayoutDashboard' },
  { path: '/accounts', label: 'Accounts', icon: 'Users' },
  { path: '/emails', label: 'Email Explorer', icon: 'Mail' },
  { path: '/engine', label: 'Engine', icon: 'Server' },
  { path: '/infrastructure', label: 'Infrastructure', icon: 'Monitor' },
  { path: '/boxes', label: 'SMTP Boxes', icon: 'HardDrive' },
  { path: '/ips', label: 'Dedicated IPs', icon: 'Network' },
  { path: '/domains', label: 'Domains', icon: 'Globe' },
  { path: '/deliverability', label: 'Deliverability', icon: 'Activity' },
  { path: '/bounces', label: 'Bounces', icon: 'AlertTriangle' },
  { path: '/abuse', label: 'Abuse Monitor', icon: 'ShieldAlert' },
  { path: '/builder-applications', label: 'Builder Apps', icon: 'ClipboardList' },
  { path: '/staging', label: 'Staging Health', icon: 'MonitorCheck' },
  { path: '/status', label: 'Status', icon: 'Activity' },
  { path: '/billing', label: 'Billing', icon: 'CreditCard' },
  { path: '/alerts', label: 'Alerts', icon: 'Bell' },
  { path: '/audit', label: 'Audit Log', icon: 'FileText' },
  { path: '/admin/users', label: 'Admin Users', icon: 'Shield', superAdminOnly: true },
  { path: '/bug-reports', label: 'Bug Reports', icon: 'Bug' },
  { path: '/settings', label: 'Settings', icon: 'Settings' },
]

interface MobileNavProps {
  open: boolean
  onClose: () => void
}

export function MobileNav({ open, onClose }: MobileNavProps): React.JSX.Element | null {
  const { isSuperAdmin } = useAuth()

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* biome-ignore lint/a11y/useSemanticElements: full-screen overlay backdrop, not a standard button */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close navigation"
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClose()
        }}
      />
      <div className="fixed inset-y-0 left-0 w-64 bg-surface-0 shadow-xl">
        <div className="flex h-16 items-center justify-between border-b border-surface-3 px-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-admin-500">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-semibold text-text-primary">Admin</span>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 hover:bg-surface-1">
            <X className="h-5 w-5 text-text-tertiary" />
          </button>
        </div>

        <nav className="space-y-1 p-3">
          {NAV_ITEMS.map((item) => {
            if (item.superAdminOnly && !isSuperAdmin) return null
            const Icon = ICON_MAP[item.icon]
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={onClose}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-admin-50 text-admin-700'
                      : 'text-text-secondary hover:bg-surface-1 hover:text-text-primary',
                  )
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
